import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../lib/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.startsWith('it');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(isItalian ? 'Le password non coincidono' : 'Passwords do not match');
      return;
    }
    if (password.length < 4) {
      toast.error(isItalian ? 'La password deve avere almeno 4 caratteri' : 'Password must be at least 4 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.resetPassword(token, password);
      toast.success(res.data?.message || (isItalian ? 'Password reimpostata!' : 'Password reset!'));
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Link non valido o scaduto' : 'Invalid or expired link'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen ept-bg-ambient flex items-center justify-center p-4">
        <div className="bg-[#0a0d15]/80 border border-red-500/20 p-8 text-center space-y-4 max-w-md">
          <p className="text-white">{isItalian ? 'Link non valido.' : 'Invalid link.'}</p>
          <Button onClick={() => navigate('/login')} className="bg-gradient-to-r from-[#00F0FF] to-[#00E676] text-black font-bold">
            {isItalian ? 'TORNA AL LOGIN' : 'BACK TO LOGIN'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#0a0d15]/80 border border-[#00F0FF]/10 p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-heading font-bold text-white tracking-wider">
              {isItalian ? 'NUOVA PASSWORD' : 'NEW PASSWORD'}
            </h1>
            <p className="text-sm text-slate-400">
              {isItalian ? 'Inserisci la tua nuova password' : 'Enter your new password'}
            </p>
          </div>

          {done ? (
            <div className="text-center space-y-4 py-4" data-testid="reset-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
                <Check className="w-8 h-8 text-[#00E676]" />
              </div>
              <p className="text-white">{isItalian ? 'Password reimpostata!' : 'Password reset!'}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-[#00F0FF] to-[#00E676] text-black font-bold hover:opacity-90"
                data-testid="go-to-login"
              >
                {isItalian ? 'VAI AL LOGIN' : 'GO TO LOGIN'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isItalian ? 'Nuova password' : 'New password'}
                  required
                  data-testid="reset-password"
                  className="pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#00F0FF]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={isItalian ? 'Conferma password' : 'Confirm password'}
                  required
                  data-testid="reset-confirm-password"
                  className="pl-10 h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-[#00F0FF] to-[#00E676] text-black font-bold hover:opacity-90"
                data-testid="reset-submit"
              >
                {loading
                  ? (isItalian ? 'SALVATAGGIO...' : 'SAVING...')
                  : (isItalian ? 'REIMPOSTA PASSWORD' : 'RESET PASSWORD')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
